import React, { useRef, useEffect, ReactNode } from "react";

interface CopyableTableWrapperProps {
  children: ReactNode;
}

const CopyableTableWrapper: React.FC<CopyableTableWrapperProps> = ({ children }) => {
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleCopy = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || !selection.toString() || !tableRef.current) return;

      let node: Node | null = selection.anchorNode;
      let isInsideTable = false;
      while (node && node !== document.body) {
        if (node === tableRef.current) {
          isInsideTable = true;
          break;
        }
        node = node.parentNode;
      }
      if (!isInsideTable) return;

      // Check if selection is within a single cell
      const range = selection.getRangeAt(0);
      const startContainer = range.startContainer;
      const endContainer = range.endContainer;

      // Find the closest cell elements for start and end
      let startCell =
        startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentElement : (startContainer as Element);
      let endCell = endContainer.nodeType === Node.TEXT_NODE ? endContainer.parentElement : (endContainer as Element);

      while (startCell && !["TD", "TH"].includes(startCell.tagName)) {
        startCell = startCell.parentElement;
      }
      while (endCell && !["TD", "TH"].includes(endCell.tagName)) {
        endCell = endCell.parentElement;
      }

      // If both start and end are in the same cell, copy only that text (handles double-click and triple-click)
      if (startCell && endCell && startCell === endCell) {
        const cellText = selection.toString().trim();
        if (e.clipboardData) {
          e.clipboardData.setData("text/plain", cellText);
          e.clipboardData.setData("text/html", cellText);
          e.preventDefault();
        }
        return;
      }

      // If we can't find cells or they're different, check if selection is actually spanning multiple cells
      // by comparing the selected text with what would be in a single cell
      if (startCell && endCell) {
        const startCellText = startCell.textContent?.trim() || "";
        const endCellText = endCell.textContent?.trim() || "";
        const selectedText = selection.toString().trim();

        // If selected text matches start cell content exactly, it's a single cell selection
        if (selectedText === startCellText || selectedText === endCellText) {
          if (e.clipboardData) {
            e.clipboardData.setData("text/plain", selectedText);
            e.clipboardData.setData("text/html", selectedText);
            e.preventDefault();
          }
          return;
        }
      }

      // Check if selection spans multiple cells in the same row
      let startRow = startCell;
      let endRow = endCell;
      while (startRow && startRow.tagName !== "TR") {
        startRow = startRow.parentElement;
      }
      while (endRow && endRow.tagName !== "TR") {
        endRow = endRow.parentElement;
      }

      // If selection is within the same row but multiple cells, copy in table format
      if (startRow && endRow && startRow === endRow && startCell && endCell && startCell !== endCell) {
        const tr = startRow as HTMLTableRowElement;
        const allCells = Array.from(tr.querySelectorAll("td"));
        const startIndex = allCells.indexOf(startCell as HTMLTableCellElement);
        const endIndex = allCells.indexOf(endCell as HTMLTableCellElement);

        if (startIndex !== -1 && endIndex !== -1) {
          const minIndex = Math.min(startIndex, endIndex);
          const maxIndex = Math.max(startIndex, endIndex);
          const selectedCells = allCells.slice(minIndex, maxIndex + 1);

          if (selectedCells.length > 1) {
            // Multiple cells selected - format as table
            const cellTexts = selectedCells.map(cell => (cell.textContent ? cell.textContent.trim() : ""));
            const plainText = cellTexts.join("\t");
            let htmlTable = '<table border="1" cellpadding="2" cellspacing="0"><tbody><tr>';
            cellTexts.forEach(cellText => {
              htmlTable += `<td>${cellText}</td>`;
            });
            htmlTable += "</tr></tbody></table>";

            if (e.clipboardData) {
              e.clipboardData.setData("text/plain", plainText);
              e.clipboardData.setData("text/html", htmlTable);
              e.preventDefault();
            }
            return;
          }
        }

        // Fallback - just copy selected text
        const selectedText = selection.toString().trim();
        if (e.clipboardData) {
          e.clipboardData.setData("text/plain", selectedText);
          e.clipboardData.setData("text/html", selectedText);
          e.preventDefault();
        }
        return;
      }

      // Existing multi-row/grid copying logic
      const selectedRows: string[][] = [];
      const rows = Array.from(tableRef.current.querySelectorAll("tbody tr"));
      rows.forEach(row => {
        const tr = row as HTMLTableRowElement;
        if (selection.containsNode(tr, true)) {
          const cells: string[] = [];
          tr.querySelectorAll("td").forEach((cell: HTMLTableCellElement) => {
            cells.push(cell.textContent ? cell.textContent.trim() : "");
          });
          if (cells.length > 0) {
            selectedRows.push(cells);
          }
        }
      });
      if (selectedRows.length === 0) return;

      // Check if header row is included in selection
      const headerRow = tableRef.current.querySelector("thead tr");
      const isHeaderSelected = headerRow && selection.containsNode(headerRow, true);

      // Include headers if header row is selected OR if multiple rows are selected
      const includeHeaders = isHeaderSelected || selectedRows.length > 1;
      const headers: string[] = [];

      if (includeHeaders) {
        const headerCells = tableRef.current.querySelectorAll("th");
        headerCells.forEach((cell: HTMLTableCellElement) => {
          headers.push(cell.textContent ? cell.textContent.trim() : "");
        });
      }

      let htmlTable = '<table border="1" cellpadding="2" cellspacing="0">';
      if (includeHeaders) {
        htmlTable += "<thead><tr>";
        headers.forEach(header => {
          htmlTable += `<th>${header}</th>`;
        });
        htmlTable += "</tr></thead>";
      }
      htmlTable += "<tbody>";
      selectedRows.forEach(rowCells => {
        htmlTable += "<tr>";
        rowCells.forEach(cell => {
          htmlTable += `<td>${cell}</td>`;
        });
        htmlTable += "</tr>";
      });
      htmlTable += "</tbody></table>";

      let plainText = "";
      if (includeHeaders) {
        headers.forEach(header => {
          plainText += `${header}\t`;
        });
        plainText += "\n";
      }
      selectedRows.forEach(rowCells => {
        rowCells.forEach(cell => {
          plainText += `${cell}\t`;
        });
        plainText += "\n";
      });
      if (e.clipboardData) {
        e.clipboardData.setData("text/html", htmlTable);
        e.clipboardData.setData("text/plain", plainText);
        e.preventDefault();
      }
    };
    document.addEventListener("copy", handleCopy as EventListener);
    return () => {
      document.removeEventListener("copy", handleCopy as EventListener);
    };
  }, []);

  return <div ref={tableRef}>{children}</div>;
};

export default CopyableTableWrapper;
